import React, { useState } from 'react';
import { Button, DatePicker, Divider, Form, Modal, Row, Select, TimePicker } from 'antd';
import './ScheduleModel.scss';
import { BackArrowIcon, ScheduleIcon, SunRise } from 'components/Icons';
import moment from 'moment-timezone';
import { useReactiveVar } from '@apollo/client';
import { userVar } from 'graphql/cache';
import useWindowDimensions from 'hooks/useWindowDimensions';
import Drawer from 'components/Drawer';

const timeZones = moment.tz.names().map((tz) => {
  const offset = moment.tz(tz).utcOffset(); // in minutes
  const sign = offset >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offset) / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
  const gmtOffset = `GMT${sign}${hours}:${minutes}`;

  return {
    label: `${tz} (${gmtOffset})`,
    value: tz,
  };
});

export const getUserDateFormatForSchedule = (user) => {
  // Always show full year (YYYY) instead of abbreviated year (YY)
  const dateFormat = user?.profile?.dateFormat || 'MM/DD/YYYY';

  /*
  REMARK: caused issue in iphone 11
  Lookbehind is NOT supported in Safari < 16
  Error : Invalid regular expression: invalid group specifier name
  */
  // const formatWithFullYear = dateFormat.replace(/(?<!Y)YY(?!Y)/g, 'YYYY');
  const formatWithFullYear = dateFormat.replace(/(^|[^Y])YY([^Y]|$)/g, '$1YYYY$2');
  return formatWithFullYear;
};

export const ScheduleModel = ({ isOpen, onCancel, loading = false, onSchedule }) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const { width } = useWindowDimensions();

  const checkFormValidity = () => {
    const values = form.getFieldsValue();
    const hasDate = values.date && moment.isMoment(values.date);
    const hasTime = values.time && moment.isMoment(values.time);
    const hasTimezone = values.timezone && values.timezone.trim() !== '';

    setIsFormValid(hasDate && hasTime && hasTimezone);
  };

  const modalContent = (
    <div className="schedule-model-wrapper">
      <Row style={{ flexDirection: 'column', gap: '32px' }} justify="center">
        <ScheduleIcon />
        <div className="title-wrapper">
          <span className="title">Schedule Your Proposal For Delivery</span>
          <div className="divider"></div>
        </div>
        <ScheduleForm form={form} onSchedule={onSchedule} onFormChange={checkFormValidity} />
      </Row>
      <Divider />
      <Row className="footer-wrapper" justify="space-between" align="middle">
        <Button
          className="large-btn grey-btn"
          onClick={onCancel}
          disabled={loading}
          icon={<BackArrowIcon className="back-tag-icon" />}>
          Back
        </Button>
        <Button
          className="large-btn secondary-btn"
          disabled={loading || !isFormValid}
          onClick={() => form.submit()}>
          Schedule
        </Button>
      </Row>
    </div>
  );
  if (width < 600) {
    return (
      <>
        <Drawer
          visible={isOpen}
          setVisible={onCancel}
          content={modalContent}
          direction="bottom"
          className="email-recipients-popover"
        />
      </>
    );
  }

  return isOpen ? (
    <Modal
      className="schedule-model-wrapper"
      maskClosable={false}
      visible
      centered={true}
      footer={null}
      onCancel={onCancel}>
      {' '}
      {modalContent}
    </Modal>
  ) : (
    <> </>
  );
};

export const ScheduleForm = ({
  form,
  onSchedule,
  initialValues,
  formDisabled = false,
  onFormChange,
  validateForm = true,
}) => {
  const userTimezone = moment.tz.guess();
  const [selectedTimeZone, setSelectedTimeZone] = useState(initialValues?.timezone || userTimezone);
  const [error, setError] = useState('');
  const nowInSelectedTZ = moment().tz(selectedTimeZone);
  const user = useReactiveVar(userVar);

  // Trigger initial validation check
  React.useEffect(() => {
    if (onFormChange) {
      // Small delay to ensure form is initialized
      setTimeout(() => {
        onFormChange();
      }, 100);
    }
  }, [onFormChange]);

  const onSubmit = (values) => {
    const { date, time } = values;
    const nowInTZ = moment().tz(selectedTimeZone);

    const combined = moment.tz(
      `${date.format('YYYY-MM-DD')} ${time.format('HH:mm')}`,
      'YYYY-MM-DD HH:mm',
      selectedTimeZone
    );

    if (validateForm && combined.isSameOrBefore(nowInTZ)) {
      setError('Please select a date and time that is in the future for the chosen time zone.');
      return;
    }

    const tz = timeZones.find((zone) => zone.value === selectedTimeZone);

    onSchedule?.({ ...values, tz }, combined);
  };

  // Disable past dates
  const disabledDate = (current) => {
    return current && current.isBefore(nowInSelectedTZ.startOf('day'), 'day');
  };

  return (
    <Form
      form={form}
      style={{ width: '100%' }}
      onValuesChange={() => {
        setError('');
        onFormChange && onFormChange();
      }}
      initialValues={
        initialValues
          ? {
              date: moment(initialValues.scheduleTime),
              time: moment(initialValues.scheduleTime),
              timezone: initialValues.timezone,
            }
          : {
              date: moment(nowInSelectedTZ)?.add(1, 'days'),
              time: moment(nowInSelectedTZ),
              timezone:
                timeZones.find((zone) => zone.value === userTimezone)?.value ||
                'America/Los_Angeles',
            }
      }
      onFinish={onSubmit}
      onFieldsChange={() => {
        onFormChange && onFormChange();
      }}
      className="proposal-schedule-settings-form">
      <Row className="picker-wrapper" justify="center">
        <Row style={{ gap: '16px', width: '100%' }}>
          <Form.Item name={'date'} noStyle>
            <DatePicker
              format={getUserDateFormatForSchedule(user)}
              showToday={false}
              disabled={formDisabled}
              disabledDate={disabledDate}
            />
          </Form.Item>
          <Form.Item name={'time'} noStyle>
            <TimePicker
              popupClassName="time-picker-schedule"
              use12Hours
              format="h:mm A"
              disabled={formDisabled}
              showNow={false}
            />
          </Form.Item>
        </Row>
        <Form.Item name={'timezone'} noStyle>
          <Select
            showSearch
            className="timezone-select"
            style={{ width: '100%', height: '40px' }}
            placeholder="Search to Select"
            optionFilterProp="label"
            suffixIcon={<SunRise />}
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '')
                .toLowerCase()
                .localeCompare((optionB?.label ?? '').toLowerCase())
            }
            onChange={(value) => {
              console.log(value, 'values');
              setSelectedTimeZone(value);
              onFormChange && onFormChange();
            }}
            options={timeZones}
            disabled={formDisabled}
          />
        </Form.Item>
        {error && <span className="txt-error">{error}</span>}
      </Row>
    </Form>
  );
};
